/**
 * Правая выезжающая панель записи (УИ-2a): просмотр, правка и создание одной
 * строки таблицы. Замена «стрёмных» центральных модалок редактирования.
 *
 * Шасси — общее с инспекторами аукциона/задач: класс `task-inspector
 * record-panel` (как tender-inspector ставит `task-inspector tdi-inspector`),
 * ширина/позиционирование/брейкпоинт — правила .task-inspector* в main.css
 * (их НЕ трогаем), своё — в styles/forms.css. Форма — на styles/forms.css
 * (.modal-grid/.modal-field), рендер полей — components/record-field.js.
 *
 *   mountRecordPanel(container, { key: 'expertise', defaultWidth: 520 });  // один раз на экран
 *   // maximized:true — стартовать развёрнутой (ИЛ, УИ-2d): карточка «как в 1С» во всю ширину
 *   openRecordPanel(record | null, {
 *     title,       // строка ИЛИ (record)=>строка — заголовок панели
 *     fields,      // схема полей (record-field.js) или (record,{isCreate})=>fields
 *     readLines,   // [{label, value}] блок «только для вывода» (справочные поля)
 *     readonly,    // просмотр: все поля disabled, без «Сохранить»/«Удалить»
 *     saveLabel,   // подпись кнопки сохранения (по умолчанию «Создать»/«Сохранить»)
 *     onSave,      // async (body,{isCreate,record}) => record — зовёт api.*.create/patch
 *     reload,      // async (record) => record — перечитать запись (GET /…/{id})
 *     onSaved,     // (record,{isCreate}) => void — обновить таблицу/тост
 *     onDelete,    // async (record) => void — показывает кнопку «Удалить»
 *     deleteMessage, onClose,
 *     resizeKey,   // УИ-2e: свой ключ памяти ширины на это открытие ('normcheck')
 *     resizeDefaultWidth,  // базовая ширина для resizeKey
 *   });
 *   closeRecordPanel();  currentRecordId();
 *
 * Отличие от наброска в плане (tabs при монтировании): «Экспертиза» держит на
 * одном экране две сущности (замечания/разрешения) с разными полями, поэтому
 * спецификацию формы передаём при ОТКРЫТИИ, а не при монтировании.
 *
 * УИ-2b — вкладки. Если в spec есть `tabs`, панель рисует переключатель (как
 * tender-inspector) и переключает содержимое:
 *   tabs: [
 *     { key, label,
 *       // вкладка-форма (как одно-форменный путь): своя схема + сохранение
 *       fields, readLines, readonly, saveLabel, onSave, reload, onSaved, onDelete, deleteMessage,
 *       // ИЛИ вкладка с произвольным HTML и жизненным циклом:
 *       render(record, ctx) }    // ctx: { host, record(get), onTeardown, rerender, setRecord, setTab }
 *   ]
 *   activeTab          // ключ стартовой вкладки
 * Без `tabs` поведение прежнее (на нём живёт «Экспертиза», её e2e зелёный).
 * Teardown активной render-вкладки (ctx.onTeardown) вызывается при переключении
 * вкладки, смене записи и закрытии — чтобы гасить таймеры опроса.
 *
 * УИ-2c — одиночный render-body. Если в spec (без `tabs`) есть `render(rec, ctx)`,
 * тело формы рисует сам вызывающий (нужно форме контрагента: набор полей зависит
 * от «Вида», живая проверка ИНН — статичной схемой `fields` это не выразить).
 * Футер при этом штатный: «Сохранить»/«Отмена», сбор тела — через `spec.collect(form)`
 * (форма должна иметь [data-rp-form]), сохранение — `spec.onSave`. Доп. действия в
 * футере — `footerActions:[{key,label,danger?}]` + `onAction(key,record)` (напр.
 * «Объединить с…»). ctx тот же, что у render-вкладки, минус setTab.
 */
import { attachInspectorResize } from '../utils/inspector-resize.js';
import { renderField, bindRefField, collectFields, escapeHtml, escapeAttr } from './record-field.js';
import { openModal } from './modal.js';

const apiErrorText = (e) => e?.detail ?? e?.message ?? 'Ошибка';

let _root = null;
let _resize = null;
let _mount = null;   // {key, defaultWidth, maximized} монтирования — чтобы вернуть ключ ширины
let _curKey = null;  // текущий ключ ширины панели (может отличаться от _mount на карточке проверки)
let _st = blank();

function blank() {
  return {
    open: false, record: null, isCreate: false, spec: null, fields: [],
    busy: false, error: null, focusReturn: null, activeTab: null, tabCtx: null,
  };
}

// Esc закрывает панель только вне окна/пикера и когда фокус не в поле ввода
// (образец — tender-inspector.js, он исключает и SELECT). Один слушатель на модуль.
document.addEventListener('keydown', (e) => {
  if (e.key !== 'Escape' || !_st.open) return;
  if (document.querySelector('dialog[open]')) return;
  const t = document.activeElement;
  if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT')) return;
  closeRecordPanel();
});

export function mountRecordPanel(container, { key, defaultWidth = 520, maximized = false } = {}) {
  if (_root && document.body.contains(_root)) return _root;
  _root = document.createElement('aside');
  _root.className = 'task-inspector record-panel';
  _root.hidden = true;
  _root.setAttribute('role', 'dialog');
  _root.setAttribute('aria-label', 'Карточка записи');
  _root.innerHTML = `
    <header class="ti-header">
      <span class="ti-title"></span>
      <button type="button" class="ti-close" title="Закрыть (Esc)" aria-label="Закрыть">✕</button>
    </header>
    <nav class="rp-tabs" role="tablist" hidden></nav>
    <div class="ti-body"></div>
    <footer class="ti-footer"></footer>`;
  _root.querySelector('.ti-close').addEventListener('click', () => closeRecordPanel());
  _root.addEventListener('click', onClick);
  _st = blank();                     // свежий экран — чистое состояние
  container.appendChild(_root);
  // attachInspectorResize требует .ti-header и .ti-close ДО вызова (вставляет
  // «Развернуть» перед крестиком) и задаёт базовую ширину через defaultWidth.
  _resize = attachInspectorResize(_root, { key, defaultWidth, maximized });
  _mount = { key, defaultWidth, maximized };
  _curKey = key;
  return _root;
}

export function openRecordPanel(record, spec = {}) {
  if (!_root) return;
  _teardownTab();                    // снять таймеры прошлой записи, если панель была открыта
  _st = {
    ...blank(), open: true, record: record ?? null, isCreate: !record, spec,
    focusReturn: document.activeElement,
    activeTab: spec.tabs ? (spec.activeTab ?? spec.tabs[0]?.key ?? null) : null,
  };
  _root.hidden = false;
  void _root.offsetWidth;            // форсируем кадр для transition
  _root.classList.add('ti-open');
  applyResizeKey(spec);              // УИ-2e: своя память ширины у карточки проверки
  _resize?.notify();
  render();
  // Автофокус первого активного поля (при создании/правке), иначе крестик.
  const first = _root.querySelector(
    '.record-panel-form input:not([disabled]):not([type="hidden"]), .record-panel-form select:not([disabled]), .record-panel-form textarea:not([disabled])',
  );
  (first ?? _root.querySelector('.ti-close')).focus();
}

export function closeRecordPanel() {
  if (!_root || !_st.open) return;
  _teardownTab();
  const { spec, focusReturn } = _st;
  _st = blank();
  _root.classList.remove('ti-open');
  _root.hidden = true;
  _root.querySelector('.rp-tabs').hidden = true;
  _resize?.notify();
  spec?.onClose?.();
  if (focusReturn && document.body.contains(focusReturn)) {
    try { focusReturn.focus(); } catch { /* элемент мог исчезнуть */ }
  }
}

export const currentRecordId = () => _st.record?.id ?? null;

/**
 * УИ-2e — ключ ширины панели на ОТКРЫТИЕ. По умолчанию панель живёт на ключе
 * монтирования (_mount.key). Если spec задаёт `resizeKey` (карточка проверки
 * «Нормоконтроля» → 'normcheck'), переключаемся на него с собственной памятью
 * ширины; при следующем открытии БЕЗ resizeKey (норматив/ИЛ) возвращаемся на
 * ключ монтирования с его ширину/«развёрнуто». Для панелей без resizeKey
 * (Экспертиза/Контрагенты) ключ всегда равен монтажному — setKey не вызывается,
 * поведение прежнее.
 */
function applyResizeKey(spec) {
  if (!_resize?.setKey || !_mount) return;
  const wantKey = spec?.resizeKey || _mount.key;
  if (wantKey === _curKey) return;
  _resize.setKey(wantKey, spec?.resizeKey
    ? { defaultWidth: spec.resizeDefaultWidth, maximized: spec.maximized ?? false }
    : { defaultWidth: _mount.defaultWidth, maximized: _mount.maximized });
  _curKey = wantKey;
}

/** Активная «форм-спека»: без вкладок — весь spec, со вкладками — текущая вкладка. */
function activeFormSpec() {
  const { spec, activeTab } = _st;
  if (!spec?.tabs) return spec;
  return spec.tabs.find(t => t.key === activeTab) || spec.tabs[0];
}

function titleText() {
  const t = _st.spec?.title;
  return (typeof t === 'function' ? t(_st.record) : t) || '';
}

function render() {
  const { spec } = _st;
  const title = titleText();
  _root.setAttribute('aria-label', title || 'Карточка записи');
  _root.querySelector('.ti-title').textContent = title;

  const nav = _root.querySelector('.rp-tabs');
  if (spec.tabs) {
    if (!_st.activeTab) _st.activeTab = spec.tabs[0]?.key ?? null;
    nav.hidden = false;
    nav.innerHTML = spec.tabs.map(t =>
      `<button type="button" role="tab" class="rp-tab" data-rp-tab="${escapeAttr(t.key)}">${escapeHtml(t.label)}</button>`).join('');
    activateTab();
    return;
  }
  nav.hidden = true;
  nav.innerHTML = '';
  if (typeof spec.render === 'function') { renderCustomInto(spec); return; }
  renderFormInto(spec);
}

/**
 * Одиночный render-body (УИ-2c): тело рисует вызывающий через spec.render(rec, ctx),
 * футер — штатный (renderFooter). Teardown, как у render-вкладки, снимаем перед
 * перерисовкой (повторный render() при правке) и при закрытии.
 */
function renderCustomInto(fs) {
  _teardownTab();
  const body = _root.querySelector('.ti-body');
  body.innerHTML = `${errorHtml()}<div class="rp-pane" data-rp-pane></div>`;
  _st.error = null;                   // ошибку показываем один раз
  const ctx = makeBodyCtx(body.querySelector('[data-rp-pane]'));
  _st.tabCtx = ctx;
  try {
    const out = fs.render(_st.record, ctx);
    if (out && typeof out.then === 'function') out.catch(() => { /* тело само отрисует ошибку */ });
  } catch { /* тело само отрисует ошибку */ }
  _root.querySelector('.ti-footer').innerHTML = renderFooter(fs);
}

/** ctx для render-body: как makeTabCtx, но rerender перерисовывает всю панель. */
function makeBodyCtx(host) {
  const ctx = {
    host,
    _teardowns: [],
    get record() { return _st.record; },
    onTeardown(fn) { if (typeof fn === 'function') ctx._teardowns.push(fn); },
    rerender() { if (_st.open && !_st.spec?.tabs) render(); },
    setRecord(rec) { if (rec) _st.record = rec; },
  };
  return ctx;
}

/** Снять teardown текущей render-вкладки (таймеры опроса и т.п.). */
function _teardownTab() {
  const ctx = _st.tabCtx;
  _st.tabCtx = null;
  if (!ctx) return;
  for (const fn of ctx._teardowns.splice(0)) { try { fn(); } catch { /* не мешаем закрытию */ } }
}

function makeTabCtx(tab, host) {
  const ctx = {
    host,
    _teardowns: [],
    get record() { return _st.record; },
    onTeardown(fn) { if (typeof fn === 'function') ctx._teardowns.push(fn); },
    rerender() { if (_st.open && _st.activeTab === tab.key) activateTab(); },
    setRecord(rec) { if (rec) _st.record = rec; },
    setTab(key) { switchTab(key); },
  };
  return ctx;
}

function activateTab() {
  _teardownTab();
  const { spec } = _st;
  const tab = spec.tabs.find(t => t.key === _st.activeTab) || spec.tabs[0];
  _st.activeTab = tab.key;
  _root.querySelectorAll('.rp-tab').forEach((b) => {
    const on = b.dataset.rpTab === tab.key;
    b.classList.toggle('is-active', on);
    b.setAttribute('aria-selected', String(on));
  });
  const body = _root.querySelector('.ti-body');
  const footer = _root.querySelector('.ti-footer');
  if (typeof tab.render === 'function') {
    footer.innerHTML = '';
    body.innerHTML = `${errorHtml()}<div class="rp-pane" data-rp-pane></div>`;
    _st.error = null;                 // ошибку показываем один раз
    const ctx = makeTabCtx(tab, body.querySelector('[data-rp-pane]'));
    _st.tabCtx = ctx;
    try {
      const out = tab.render(_st.record, ctx);
      if (out && typeof out.then === 'function') out.catch(() => { /* тело вкладки сам отрисует ошибку */ });
    } catch { /* тело вкладки сам отрисует ошибку */ }
    return;
  }
  renderFormInto(tab);
}

const errorHtml = () => (_st.error ? `<div class="ti-error" data-rp-error>${escapeHtml(_st.error)}</div>` : '');

function renderFormInto(fs) {
  const { record, isCreate } = _st;
  const base = typeof fs.fields === 'function' ? fs.fields(record, { isCreate }) : (fs.fields ?? []);
  const fields = fs.readonly ? base.map(f => ({ ...f, readonly: true })) : base;
  _st.fields = fields;

  const readLines = ((typeof fs.readLines === 'function' ? fs.readLines(record) : fs.readLines) ?? []).filter(Boolean);
  const readBlock = readLines.length
    ? `<div class="record-panel-readonly">${readLines.map(l =>
        `<div class="ti-field"><span class="ti-label">${escapeHtml(l.label)}</span>
          <span class="ti-value">${l.value == null || l.value === '' ? '—' : escapeHtml(String(l.value))}</span></div>`).join('')}</div>`
    : '';

  _root.querySelector('.ti-body').innerHTML = `
    ${errorHtml()}
    ${readBlock}
    <form class="record-panel-form modal-grid" data-rp-form novalidate>
      ${fields.map(renderField).join('')}
    </form>`;

  const form = _root.querySelector('[data-rp-form]');
  form.addEventListener('submit', (e) => e.preventDefault());
  for (const f of fields) bindRefField(form, f);

  _root.querySelector('.ti-footer').innerHTML = renderFooter(fs);
}

function renderFooter(fs) {
  const { isCreate, busy } = _st;
  if (fs.readonly) {
    return '<button type="button" class="ti-btn" data-rp-close>Закрыть</button>';
  }
  const save = `<button type="button" class="ti-btn ti-btn-primary" data-rp-save${busy ? ' disabled' : ''}>${escapeHtml(fs.saveLabel || (isCreate ? 'Создать' : 'Сохранить'))}</button>`;
  const cancel = '<button type="button" class="ti-btn" data-rp-close>Отмена</button>';
  const del = (!isCreate && fs.onDelete)
    ? `<button type="button" class="ti-btn ti-btn-danger" data-rp-delete${busy ? ' disabled' : ''}>Удалить</button>` : '';
  // Доп. действия (УИ-2c, напр. «Объединить с…») — только у существующей записи.
  const extra = (!isCreate && Array.isArray(fs.footerActions))
    ? fs.footerActions.filter(Boolean).map(a =>
        `<button type="button" class="ti-btn${a.danger ? ' ti-btn-danger' : ''}" data-rp-action="${escapeAttr(a.key)}"${busy ? ' disabled' : ''}>${escapeHtml(a.label)}</button>`).join('')
    : '';
  return `${save}${cancel}${extra}${del}`;
}

function switchTab(key) {
  if (!_st.spec?.tabs || key === _st.activeTab) return;
  if (!_st.spec.tabs.some(t => t.key === key)) return;
  _st.error = null;
  _st.activeTab = key;
  activateTab();
}

function onClick(e) {
  const tabBtn = e.target.closest?.('[data-rp-tab]');
  if (tabBtn) { switchTab(tabBtn.dataset.rpTab); return; }
  if (e.target.closest?.('[data-rp-close]')) { closeRecordPanel(); return; }
  if (e.target.closest?.('[data-rp-save]')) { save(); return; }
  if (e.target.closest?.('[data-rp-delete]')) { remove(); return; }
  const actBtn = e.target.closest?.('[data-rp-action]');
  if (actBtn) { activeFormSpec()?.onAction?.(actBtn.dataset.rpAction, _st.record); return; }
}

async function save() {
  if (_st.busy) return;
  const fs = activeFormSpec();
  const isCreate = _st.isCreate;
  const form = _root.querySelector('[data-rp-form]');
  if (!form || !form.reportValidity()) return;   // нативная проверка required
  // render-body (УИ-2c) собирает тело сам через spec.collect; иначе — по схеме fields.
  const body = fs.collect ? fs.collect(form) : collectFields(form, _st.fields);
  _st.busy = true; _st.error = null; render();
  try {
    const saved = await fs.onSave(body, { isCreate, record: _st.record });
    _st.busy = false;
    // Без вкладок: создание закрывает панель (строка появится в таблице),
    // правка — перечитывает запись и остаётся. На этом живёт «Экспертиза».
    if (!_st.spec.tabs) {
      const fresh = (!isCreate && fs.reload) ? await fs.reload(saved ?? _st.record) : (saved ?? _st.record);
      fs.onSaved?.(fresh, { isCreate });
      if (isCreate) { closeRecordPanel(); return; }
      _st.record = fresh; _st.isCreate = false; render();
      return;
    }
    // Со вкладками: панель остаётся и при создании (нужно заполнить другие
    // вкладки, напр. загрузить редакцию) — перечитываем и даём onSaved сменить вкладку.
    const fresh = fs.reload ? await fs.reload(saved ?? _st.record) : (saved ?? _st.record);
    _st.record = fresh; _st.isCreate = false;
    const setTab = (key) => { if (_st.spec.tabs.some(t => t.key === key)) _st.activeTab = key; };
    fs.onSaved?.(fresh, { isCreate, setTab });
    render();
  } catch (e) {
    _st.busy = false;
    if (e.message !== '401') _st.error = apiErrorText(e);
    render();
  }
}

async function remove() {
  if (_st.busy) return;
  const fs = activeFormSpec();
  const record = _st.record;
  const ok = await openModal({
    title: 'Удалить запись?',
    message: fs.deleteMessage || 'Запись будет удалена без возможности восстановления.',
    confirmLabel: 'Удалить', danger: true,
  });
  if (!ok || !_st.open) return;
  _st.busy = true; _st.error = null; render();
  try {
    await fs.onDelete(record);
    _st.busy = false;
    fs.onSaved?.(null, { isCreate: false, deleted: true });
    closeRecordPanel();
  } catch (e) {
    _st.busy = false;
    if (e.message !== '401') _st.error = apiErrorText(e);
    render();
  }
}
